import { FormikProps } from 'formik'
import { PlanType, TextInputV4, Truncate } from '@labrav/react-components'
import { get } from 'lodash'
import { useStyles } from './AdditionalUsageDimensionsV2.styles'
import { Dimension } from '../../../types/privateOffer'
import { Typography } from '@mui/material'
import clsx from 'clsx'
import { memo } from 'react'

interface AdditionalUsageDimensionsV2Props {
  formik: FormikProps<Record<string, unknown>>
  dimensionsHeadingName?: string
  shouldShowDescription?: boolean
  inputFieldTitle?: string
  isResale?: boolean
  heading?: string
  productType?: PlanType
  shouldShowHeading?: boolean
}

function shouldShowAdditionalUsageRow(
  dimension: Dimension,
  isResale: boolean | undefined,
  isSaasSubscription: boolean
): boolean {
  if (isResale && isSaasSubscription) {
    return true
  }
  return (
    dimension.type === 'additional_usage' ||
    !!dimension.isAdditionalUsageAllowed
  )
}

function getPriceFieldPath(
  index: number,
  isResale: boolean | undefined,
  isSaasSubscription: boolean
): string {
  if (isResale) {
    return isSaasSubscription
      ? `dimensions.${index}.prices.subscriptionPrice`
      : `dimensions.${index}.prices.additionalUsagePrice`
  }
  return `dimensions.${index}.price`
}

type DimensionRowProps = {
  dimension: Dimension
  index: number
  dimensionsLength: number
  shouldShowDescription: boolean
  inputFieldTitle: string
  priceFieldPath: string
  priceValue: unknown
  priceError: boolean
  priceFieldText: string
  handleChange: FormikProps<Record<string, unknown>>['handleChange']
  handleBlur: FormikProps<Record<string, unknown>>['handleBlur']
  classes: ReturnType<typeof useStyles>['classes']
}

const DimensionRow = memo(function DimensionRow({
  dimension,
  index,
  dimensionsLength,
  shouldShowDescription,
  inputFieldTitle,
  priceFieldPath,
  priceValue,
  priceError,
  priceFieldText,
  handleChange,
  handleBlur,
  classes,
}: DimensionRowProps) {
  return (
    <>
      <div className={classes.resourceRow} data-testid="dimensions-row">
        <div className={classes.resourcesContainer}>
          <div
            className={clsx(classes.alignDescription)}
            style={{ width: '42%', height: '56px' }}
            data-testid="description-label"
          >
            <Truncate width="250px" clipboardTooltip>
              {dimension.label}
            </Truncate>
          </div>

          {shouldShowDescription && (
            <div
              className={clsx(classes.alignDescription)}
              style={{ width: '42%', height: '56px' }}
              data-testid="description-labelDescription"
            >
              <Truncate width="200px" clipboardTooltip>
                {dimension.labelDescription ?? '-'}
              </Truncate>
            </div>
          )}

          <div
            className={clsx(classes.alignDescription)}
            style={{ width: '42%', height: '56px' }}
          >
            <TextInputV4
              keyName={priceFieldPath}
              title={inputFieldTitle}
              type="number"
              placeHolder=""
              value={priceValue as number}
              isError={priceError}
              errorText={priceFieldText}
              onChange={handleChange}
              isReadOnly={false}
              isRequired={false}
              showSpinner={false}
              onBlur={handleBlur}
              keyToPrevent={['+', '-', 'e']}
            />
          </div>
        </div>
      </div>

      {index !== dimensionsLength - 1 && <div className={classes.border} />}
    </>
  )
})

export const AdditionalUsageDimensionsV2 = ({
  formik,
  dimensionsHeadingName = 'Name',
  shouldShowDescription = true,
  inputFieldTitle = 'Price',
  isResale,
  heading = 'Set pricing for additional usage beyond the contract if the buyer exceeds the limit.',
  productType,
  shouldShowHeading = true,
}: AdditionalUsageDimensionsV2Props) => {
  const { classes } = useStyles()
  const { values, errors, touched, handleChange, handleBlur } = formik
  const dimensions = (values.dimensions || []) as Dimension[]

  const isSaasSubscription = productType === PlanType.SAAS_SUBSCRIPTION

  return (
    <>
      {shouldShowHeading && (
        <Typography className={classes.value}>{heading}</Typography>
      )}
      <div className={clsx(classes.root, classes.elementsGap)}>
        <div className={classes.titleWraper}>
          <Typography
            className={clsx(classes.sectionText, classes.dimensionsHeadingName)}
          >
            {dimensionsHeadingName}
          </Typography>
          {shouldShowDescription && (
            <Typography
              className={clsx(
                classes.sectionText,
                classes.dimensionsHeadingName
              )}
              data-testid="description-column"
            >
              Description
            </Typography>
          )}
          <Typography
            className={clsx(classes.sectionText, classes.dimensionsHeadingName)}
          >
            {inputFieldTitle}
          </Typography>
        </div>
        <div className={classes.border} />
        {dimensions.map((dimension, index) => {
          if (
            !shouldShowAdditionalUsageRow(
              dimension,
              isResale,
              isSaasSubscription
            )
          ) {
            return null
          }

          const priceFieldPath = getPriceFieldPath(
            index,
            isResale,
            isSaasSubscription
          )
          const priceValue = get(values, priceFieldPath, '')
          const isTouched = !!get(touched, priceFieldPath, false)
          const errorAtPath = get(errors, priceFieldPath, false)
          const priceError = !!(isTouched && errorAtPath)
          const priceFieldText =
            (isTouched && (get(errors, priceFieldPath, '') as string)) || ''

          return (
            <DimensionRow
              key={
                dimension.dimensionId ??
                dimension.id ??
                `dimension-row-${index}`
              }
              dimension={dimension}
              index={index}
              dimensionsLength={dimensions.length}
              shouldShowDescription={shouldShowDescription}
              inputFieldTitle={inputFieldTitle}
              priceFieldPath={priceFieldPath}
              priceValue={priceValue}
              priceError={priceError}
              priceFieldText={priceFieldText}
              handleChange={handleChange}
              handleBlur={handleBlur}
              classes={classes}
            />
          )
        })}
      </div>
    </>
  )
}
